/**
 * The deployed app's AWS settings (T074a), built into the app by `scripts/deploy-app.sh` from the
 * stack's outputs. None is a secret, and none is ever committed or given to previews, CI or cloud
 * sessions (CLAUDE.md data safety rule 1).
 */
export interface AwsConfig {
  region: string;
  dataBucket: string;
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId: string;
  adminFunctionName: string;
  /** The `assist` function (User Story 8); absent for a stack deployed before it existed. */
  assistFunctionName?: string;
}

const SETTINGS: Record<Exclude<keyof AwsConfig, 'assistFunctionName'>, string> = {
  region: 'VITE_AWS_REGION',
  dataBucket: 'VITE_DATA_BUCKET',
  userPoolId: 'VITE_USER_POOL_ID',
  userPoolClientId: 'VITE_USER_POOL_CLIENT_ID',
  identityPoolId: 'VITE_IDENTITY_POOL_ID',
  adminFunctionName: 'VITE_ADMIN_FUNCTION_NAME',
};

/** Reads every setting; throws naming all the missing ones, so a bad build fails loudly. */
export function readAwsConfig(env: Record<string, string | undefined>): AwsConfig {
  const missing = Object.values(SETTINGS).filter((name) => !env[name]?.trim());
  if (missing.length > 0) {
    throw new Error(`Missing AWS settings in this build: ${missing.join(', ')}.`);
  }
  const required = Object.fromEntries(
    Object.entries(SETTINGS).map(([field, name]) => [field, env[name]!.trim()]),
  ) as unknown as AwsConfig;
  // Optional: without it the app simply offers no label scan ("None" is what an older stack's
  // missing output reads as).
  const assist = env.VITE_ASSIST_FUNCTION_NAME?.trim();
  return assist && assist !== 'None' ? { ...required, assistFunctionName: assist } : required;
}
